import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useSearchParams } from 'react-router-dom';
import { useInstanceId } from '../contexts/InstanceContext';
import { useTranslation } from '../hooks/useTranslation.jsx';
import {
  MONO_FONT, Container, SVGContainer, TopLeftBar, Title, InstanceSelect, TopRightControls,
  ModeButton, FilterBar, FilterChip, StyleSwitcher, StyleButton, ListOrderBar,
  ListOrderButton, StatusMessage, UsageStatsPanel, DetailPanel, DetailTitle, DetailType,
  DetailMeta, DetailDescription, CloseButton, DetailTotal, DetailTotalLabel, ContextRow
} from './CorpusTietopohjaV4Parts';
import API_ENDPOINTS from '../config/api';

// specs/rag-source-heatmap.md — v4. A fresh module from v2/v3 per CLAUDE.md's
// redesign rule: one merged canvas instead of two panels, real continuous
// physics (drift + drag) instead of a static deterministic layout. v1/v2/v3
// stay exactly as shipped.
//
// Positions come pre-converged from the backend (seeded, reproducible —
// apps/backend/src/utils/ragSourceMapV4Physics.js) so there is no cold-start
// solve here, only a gentle continuous "drift" simulation at rest plus
// drag-to-reposition. Because that means potentially hundreds of circles
// ticking indefinitely, this file deliberately goes back to v1's proven
// imperative-d3 rendering (RagSourceMapScreen.jsx: build the SVG scaffold
// once, then update positions via direct DOM attrs on tick) rather than
// v2/v3's clean declarative React — re-rendering the whole node list through
// React state on every animation frame is a real, untested performance risk
// at this node count; v1's pattern is already proven in this codebase.
//
// SCOPE NOTE: no auto-refresh polling (v1/v2/v3 all poll every 20s; this is
// a single fetch on load). A continuously-drifting/draggable simulation
// reconciling live incoming data mid-session is real added complexity this
// pass didn't take on — this screen is closer in spirit to a workspace tool
// than a live workshop display anyway. Still reads v2/v3's same
// instanceId-scoped identity model, not a real corpus_id (same flagged
// simplification as v3, unresolved until the corpus-versioning backend exists).

const HIT_RADIUS = 22;
const FILL_OPACITY = 0.6;
const DRIFT_ALPHA_TARGET = 0.003; // never fully stops — "gentle continuous drift, never jitter"
const VELOCITY_DECAY = 0.65; // high friction so the drift reads as gentle, not jittery
const LABEL_MARGIN = 26; // gap between a cluster's topmost node and its label
const RADIUS_DAMPING_EXPONENT = 0.35; // on-screen radius grows as k^(1-this) — sub-linear, so gaps open up faster than orbs grow when zooming in
const TITLE_LABEL_SCREEN_THRESHOLD = 30; // px on-screen radius before a source's own title appears
const MODE_SETTLE_ALPHA = 0.3; // brief energy boost so the collision force visibly reacts when a mode switch changes every radius at once
const MODE_SETTLE_DURATION_MS = 900;
const BIG_MODE_MIN_RADIUS = 8; // an unused/zero-breadth source in big mode — stays small
const BIG_MODE_RATIO = 8 / 1.5; // "1.5:8" — a gentler max:min ratio (~5.3x) than the first cut's 1:12, still fed straight into the collision force
const BIG_MODE_ZOOM_MULTIPLIER = 1.5; // extra zoom-in on top of the normal bounds-fit — smaller than before since the layout itself now spreads out much more to make room for the biggest circles

// Big mode's own size scale — deliberately not the same curve Käyttö mode
// uses (sqrt, which compresses toward the top so most sources look
// similarly "medium"). Squaring the normalized breadth keeps anything below
// the top of the range close to BIG_MODE_MIN_RADIUS and reserves real growth
// for the few sources near max breadth, i.e. "small stay small, only the
// biggest get big" rather than an evenly graded scale.
function bigModeRadius(breadth, maxBreadth) {
  if (!maxBreadth || breadth <= 0) return BIG_MODE_MIN_RADIUS;
  const t = Math.min(breadth / maxBreadth, 1);
  return BIG_MODE_MIN_RADIUS * (1 + (BIG_MODE_RATIO - 1) * t * t);
}

function contextLabel(t, context) {
  const key = `ragSourceMap.contexts.${context}`;
  const translated = t(key);
  if (translated == null || translated === key) {
    return context.charAt(0).toUpperCase() + context.slice(1).replace(/_/g, ' ');
  }
  return translated;
}

function truncateTitle(title, max = 34) {
  if (!title) return '';
  return title.length > max ? `${title.slice(0, max - 1)}…` : title;
}

// Per-cluster bounding box from actual node positions (not the physics
// target center, which sits inside the cluster and is why labels used to
// render on top of the orbs) — sized by modeBRadius (the larger of the two
// size modes) so the label clears the cluster in either mode.
function computeClusterBounds(nodes, clusterOrder) {
  const bounds = {};
  clusterOrder.forEach(key => {
    const members = nodes.filter(n => n.cluster === key);
    if (members.length === 0) { bounds[key] = null; return; }
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    members.forEach(n => {
      minX = Math.min(minX, n.x - n.modeBRadius);
      maxX = Math.max(maxX, n.x + n.modeBRadius);
      minY = Math.min(minY, n.y - n.modeBRadius);
      maxY = Math.max(maxY, n.y + n.modeBRadius);
    });
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const r = Math.max(maxX - minX, maxY - minY) / 2;
    bounds[key] = { minX, maxX, minY, maxY, cx, cy, r };
  });
  return bounds;
}

// Cluster title collision avoidance — needed once clusters sit closer
// together (per live feedback tightening the zigzag spacing): a label
// anchored purely to "above my own cluster's topmost node" can land inside a
// neighboring row's territory once rows are close, or two same-row labels'
// text can simply be wider than the gap between their clusters. `rowSign` is
// -1 (push label up, away from the shared middle) for a top-row cluster or
// +1 (push down) for a bottom-row one — diverging labels away from the
// crowded center handles the common case; the pairwise nudge below then
// catches same-row neighbors whose text is wider than the horizontal gap.
function resolveClusterLabelPositions(labels) {
  const placed = [];
  for (const label of labels) {
    let { x, y } = label;
    for (let attempt = 0; attempt < 20; attempt++) {
      const box = { x0: x - label.width / 2, x1: x + label.width / 2, y0: y - label.height / 2, y1: y + label.height / 2 };
      const collision = placed.find(p => box.x0 < p.x1 && box.x1 > p.x0 && box.y0 < p.y1 && box.y1 > p.y0);
      if (!collision) break;
      const dx = x - collision.cx;
      if (dx !== 0) x += Math.sign(dx) * 10;
      else { x += (attempt % 2 === 0 ? 1 : -1) * 10; y += label.rowSign * 6; }
    }
    placed.push({ x0: x - label.width / 2, x1: x + label.width / 2, y0: y - label.height / 2, y1: y + label.height / 2, cx: x, cy: y });
    label.x = x;
    label.y = y;
  }
  return labels;
}

// Alternative visualization: an ordered grid instead of force-clustered
// orbs. Four orderings; the grid position itself is what the physics
// simulation animates toward (see targetFor in the scaffold effect) — this
// only decides WHICH slot each node gets, never x/y directly.
const LIST_TEEMA_ORDER = ['teknologia', 'ilmasto', 'demokratia', 'yhteiskunta', 'governance'];

function orderNodesForList(nodes, order, clusterOrder) {
  const arr = [...nodes];
  if (order === 'theme') {
    arr.sort((a, b) => {
      const rank = (n) => { const i = LIST_TEEMA_ORDER.indexOf(n.teema); return i === -1 ? LIST_TEEMA_ORDER.length : i; };
      return rank(a) - rank(b) || String(a.id).localeCompare(String(b.id));
    });
  } else if (order === 'size') {
    arr.sort((a, b) => b.breadth - a.breadth || String(a.id).localeCompare(String(b.id)));
  } else if (order === 'cluster') {
    // Same grouping the cluster visualization itself uses (and the same
    // colors — "cluster" and "color" are the same axis here).
    arr.sort((a, b) => {
      const rank = (n) => { const i = clusterOrder.indexOf(n.cluster); return i === -1 ? clusterOrder.length : i; };
      return rank(a) - rank(b) || String(a.id).localeCompare(String(b.id));
    });
  } else if (order === 'random') {
    // Fresh Math.random() shuffle each time — re-picking "random" is meant
    // to give a genuinely different arrangement, unlike the deterministic
    // seeded cluster layout this screen otherwise guarantees.
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }
  return arr;
}

function computeListLayout(nodes, order, clusterOrder, cellSize) {
  const ordered = orderNodesForList(nodes, order, clusterOrder);
  const cols = Math.max(1, Math.round(Math.sqrt(ordered.length * 1.6)));
  const rows = Math.ceil(ordered.length / cols);
  const positions = new Map();
  ordered.forEach((n, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    positions.set(n.id, { x: (col - (cols - 1) / 2) * cellSize, y: (row - (rows - 1) / 2) * cellSize });
  });
  const halfW = (cols * cellSize) / 2;
  const halfH = (rows * cellSize) / 2;
  return { positions, bounds: { minX: -halfW, maxX: halfW, minY: -halfH, maxY: halfH } };
}


/**
 * @param {boolean} [embedded] true when rendered inside another surface (the
 *   dashboard's Tietopohja overlay, specs/workshop-dashboard.md): the title
 *   and the instance picker stay out, and the picker's instance-list fetch
 *   is skipped. Everything else behaves exactly as on the workspace route.
 */
function CorpusTietopohjaV4Screen({ embedded = false }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const instanceId = useInstanceId(searchParams);

  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [instances, setInstances] = useState([]);
  const [mode, setMode] = useState('lahteet'); // 'lahteet' | 'kaytto'
  const [teemaFilter, setTeemaFilter] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);
  const [vizStyle, setVizStyle] = useState('cluster'); // 'cluster' | 'list'
  const [listOrder, setListOrder] = useState('size'); // 'theme' | 'size' | 'cluster' | 'random'
  const [bigMode, setBigMode] = useState(false);

  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const detailPanelRef = useRef(null);
  const layersRef = useRef(null);
  const simulationRef = useRef(null);
  const zoomBehaviorRef = useRef(null);
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const modeRef = useRef(mode);
  const teemaFilterRef = useRef(teemaFilter);
  const transformRef = useRef(d3.zoomIdentity);
  const dataRef = useRef(null);
  const defaultTransformRef = useRef(d3.zoomIdentity);
  const reducedMotionRef = useRef(false);
  const settleTimeoutRef = useRef(null);
  const vizStyleRef = useRef(vizStyle);
  const listOrderRef = useRef(listOrder);
  const listPositionsRef = useRef(new Map());
  const listBoundsRef = useRef(null);
  const bigModeRef = useRef(bigMode);

  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { teemaFilterRef.current = teemaFilter; }, [teemaFilter]);
  useEffect(() => { vizStyleRef.current = vizStyle; }, [vizStyle]);
  useEffect(() => { bigModeRef.current = bigMode; }, [bigMode]);
  useEffect(() => { listOrderRef.current = listOrder; }, [listOrder]);

  useEffect(() => {
    reducedMotionRef.current = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }, []);

  // Close the detail panel on any click/tap outside it — but not on a node
  // itself, since selecting a different source already has its own d3 click
  // handler (mousedown fires before that click, so this only ever cancels a
  // selection when the click genuinely lands elsewhere). Must be a CAPTURE-
  // phase listener: d3-zoom's own mousedown handler on the <svg> (almost the
  // entire screen) calls stopImmediatePropagation() to claim the gesture,
  // which silently swallows a bubble-phase document listener before it ever
  // runs (same gotcha v1's RagSourceMapScreen.jsx documents).
  useEffect(() => {
    if (!selectedNode) return undefined;
    const handleOutside = (event) => {
      if (detailPanelRef.current && detailPanelRef.current.contains(event.target)) return;
      if (event.target.closest && event.target.closest('.v4-hit')) return;
      setSelectedNode(null);
    };
    document.addEventListener('mousedown', handleOutside, true);
    document.addEventListener('touchstart', handleOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside, true);
      document.removeEventListener('touchstart', handleOutside, true);
    };
  }, [selectedNode]);

  // Fetch once — no polling (see file header note).
  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    fetch(API_ENDPOINTS.RAG_SOURCE_MAP_V4(instanceId))
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(json => {
        if (cancelled) return;
        setData(json);
        setStatus(json.nodes.length > 0 ? 'ready' : 'empty');
      })
      .catch(err => {
        if (!cancelled) { console.error('CorpusTietopohjaV4Screen: failed to load', err); setStatus('error'); }
      });
    return () => { cancelled = true; };
  }, [instanceId]);

  // Instance picker — lets this workspace tool be pointed at any instance
  // without hand-editing the URL. Only 31 non-deleted instances in dev today,
  // so a plain sorted <select> is enough; no search needed.
  useEffect(() => {
    if (embedded) return undefined; // no picker, no list to fetch
    let cancelled = false;
    fetch(API_ENDPOINTS.INSTANCES())
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(list => {
        if (cancelled) return;
        const sorted = (Array.isArray(list) ? list : [])
          .filter(i => !i.deleted_at)
          .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        setInstances(sorted);
      })
      .catch(err => {
        if (!cancelled) console.error('CorpusTietopohjaV4Screen: failed to load instances', err);
      });
    return () => { cancelled = true; };
  }, [embedded]);

  const handleInstanceChange = (id) => {
    if (!id || id === instanceId) return;
    setSelectedNode(null);
    setData(null);
    setStatus('loading');
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('kid', id);
      return next;
    });
  };

  // Container size tracking
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => { dimensionsRef.current = { width: el.clientWidth, height: el.clientHeight }; };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Build the SVG scaffold + simulation once data first arrives.
  useEffect(() => {
    if (!data || !svgRef.current) return undefined;
    dataRef.current = data;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const defs = svg.append('defs');
    // Subtle glow — "depth, not bloom": small blur, no bright bloom merge trick.
    const glow = defs.append('filter').attr('id', 'v4-glow').attr('x', '-50%').attr('y', '-50%').attr('width', '200%').attr('height', '200%');
    glow.append('feGaussianBlur').attr('stdDeviation', 2.2).attr('result', 'blur');
    const feMerge = glow.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'blur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    const zoomRoot = svg.append('g').attr('class', 'v4-zoom-root');
    const layers = {
      clusterLayer: zoomRoot.append('g').attr('class', 'v4-clusters'),
      nodeLayer: zoomRoot.append('g').attr('class', 'v4-nodes'),
      // Screen-space, OUTSIDE zoomRoot — per-source title labels at high
      // zoom need a constant on-screen size/position math, not the ambient
      // SVG transform (same reasoning v1's RagSourceMapScreen.jsx documents
      // for its own label layer).
      labelLayer: svg.append('g').attr('class', 'v4-node-labels').style('pointer-events', 'none'),
    };
    layersRef.current = layers;
    // The "Kuutio #n tietopohja" title is a fixed HTML overlay (<Title> in
    // the JSX below), not in-scene — per feedback it was getting in the way
    // panning underneath the graph; only the per-cluster labels stay in-scene.

    const clusterBounds = computeClusterBounds(data.nodes, data.clusterOrder);

    // Perustietopohja gets a distinct backdrop — a soft tinted disc plus a
    // dashed boundary ring, drawn behind every node layer — so it reads as
    // categorically different (the shared foundation) rather than looking
    // like one more topic cluster among the other five.
    const baseBounds = clusterBounds[data.perustietopohjaKey];
    if (baseBounds) {
      const baseColor = data.clusterColors[data.perustietopohjaKey];
      const padding = 40;
      layers.clusterLayer.append('circle')
        .attr('class', 'v4-perustietopohja-backdrop')
        .attr('cx', baseBounds.cx).attr('cy', baseBounds.cy).attr('r', baseBounds.r + padding)
        .attr('fill', baseColor).attr('fill-opacity', 0.05)
        .attr('stroke', baseColor).attr('stroke-opacity', 0.35).attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,5')
        .style('pointer-events', 'none');
    }

    // Cluster labels — anchored above each cluster's actual node extent (not
    // the physics target center, which sits inside the cluster and used to
    // render the label on top of the orbs, unreadable), then collision-
    // resolved against each other (resolveClusterLabelPositions) as a safety
    // net — cheap and harmless even when, as on the restored ring layout,
    // it rarely needs to do anything. Perustietopohja stays bolder/higher-
    // opacity than the rest, per spec — it's the shared, non-topic-specific
    // foundation.
    const labelDescriptors = data.clusterOrder
      .map((key) => {
        const bounds = clusterBounds[key];
        if (!bounds) return null;
        const isBase = key === data.perustietopohjaKey;
        const text = (isBase ? t('ragSourceMapV2.perustietopohja') : t(`ragSourceMapV2.voices.${key}`)).toUpperCase();
        const charWidth = isBase ? 9.2 : 7.2;
        return {
          key, text, isBase, rowSign: -1,
          x: bounds.cx,
          y: bounds.minY - LABEL_MARGIN,
          width: text.length * charWidth + 10,
          height: isBase ? 20 : 16,
        };
      })
      .filter(Boolean);
    resolveClusterLabelPositions(labelDescriptors);

    labelDescriptors.forEach(({ key, text, isBase, x, y }) => {
      layers.clusterLayer.append('text')
        .attr('class', `v4-cluster-label-${key}`)
        .attr('x', x).attr('y', y)
        .attr('text-anchor', 'middle')
        .attr('fill', data.clusterColors[key])
        .attr('fill-opacity', isBase ? 1 : 0.7)
        .style('font-family', MONO_FONT)
        .style('font-size', isBase ? '16px' : '12px')
        .style('font-weight', isBase ? 700 : 500)
        .style('letter-spacing', '0.05em')
        .style('text-transform', 'uppercase')
        .style('pointer-events', 'none')
        .text(text);
    });

    const simNodes = data.nodes.map(n => ({ ...n, fx: null, fy: null }));

    const drag = d3.drag()
      .on('start', (event, d) => {
        if (!event.active) simulationRef.current?.alphaTarget(0.3).restart();
        d.fx = d.x; d.fy = d.y;
      })
      .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
      .on('end', (event, d) => {
        if (!event.active) simulationRef.current?.alphaTarget(DRIFT_ALPHA_TARGET);
        d.fx = null; d.fy = null;
      });

    // Big mode overrides whatever Lähteet/Käyttö would otherwise size a node
    // at — it's fed straight into the collision force below (not a cosmetic
    // multiplier layered on top), so genuinely-larger orbs physically push
    // their neighbors apart to stay separate, the same mechanism a mode
    // switch or a drag already uses.
    const radiusFor = (d) => {
      if (bigModeRef.current) return bigModeRadius(d.breadth, data.maxBreadth);
      return modeRef.current === 'kaytto' ? d.modeBRadius : d.modeARadius;
    };
    const isFilteredOut = (d) => {
      const filter = teemaFilterRef.current;
      return filter && filter.size > 0 && d.teema && !filter.has(d.teema);
    };
    // On-screen radius grows sub-linearly with zoom (k^(1 - RADIUS_DAMPING_EXPONENT)
    // once composed with the ambient <g> transform's own k scaling below) — orbs
    // grow more slowly than the zoom itself, so the gaps between them visibly
    // open up as you zoom in, instead of the whole picture just scaling uniformly.
    // Big mode's multiplier is applied here — cosmetic/rendering only, never
    // fed into radiusFor/the collision force, so it doesn't fight or reflow
    // the cluster/list layout; it just draws everything much bigger in place.
    const dampedRadius = (d) => radiusFor(d) * (transformRef.current.k ** -RADIUS_DAMPING_EXPONENT);

    const nodeSel = layers.nodeLayer.selectAll('g.v4-node')
      .data(simNodes, d => d.id)
      .join(enter => {
        const g = enter.append('g').attr('class', 'v4-node').style('cursor', 'pointer');
        g.append('circle').attr('class', 'v4-hit')
          .attr('r', d => Math.max(d.modeARadius, HIT_RADIUS))
          .attr('fill', 'transparent')
          .on('click', (event, d) => setSelectedNode(d))
          .append('title').text(d => d.title);
        g.append('circle').attr('class', 'v4-body').style('pointer-events', 'none');
        g.append('text').attr('class', 'v4-number')
          .attr('text-anchor', 'middle').attr('dominant-baseline', 'central')
          .style('font-family', MONO_FONT).style('font-weight', 600)
          .style('pointer-events', 'none').style('fill', '#0a0a12');
        g.call(drag);
        return g;
      });

    nodeSel.select('circle.v4-body')
      .attr('fill', d => (d.retrieved || modeRef.current === 'lahteet' ? d3.color(dataRef.current.clusterColors[d.cluster])?.formatHex() : 'none'))
      .attr('fill-opacity', d => (d.retrieved ? FILL_OPACITY : (modeRef.current === 'lahteet' ? FILL_OPACITY : 0)))
      .attr('stroke', d => dataRef.current.clusterColors[d.cluster])
      .attr('stroke-opacity', d => (d.retrieved ? 0.85 : 0.6))
      .attr('stroke-width', d => (d.retrieved ? 1.2 : 1.6))
      .attr('filter', d => (d.retrieved ? 'url(#v4-glow)' : null));

    layersRef.current.nodeSel = nodeSel;

    // Estimated on-screen text width for the title-label background pill —
    // monospace, so a char-count heuristic (same approach v1 uses) is close
    // enough without real DOM text metrics.
    function worldToScreen(x, y) {
      const tr = transformRef.current;
      return { x: x * tr.k + tr.x, y: y * tr.k + tr.y };
    }

    function updateNodeLabels() {
      const k = transformRef.current.k;
      const qualifying = simNodes.filter(d => !isFilteredOut(d) && dampedRadius(d) * k >= TITLE_LABEL_SCREEN_THRESHOLD);

      const sel = layers.labelLayer.selectAll('g.v4-title-label').data(qualifying, d => d.id);
      sel.exit().remove();
      const entered = sel.enter().append('g').attr('class', 'v4-title-label');
      entered.append('rect').attr('rx', 4).attr('fill', 'rgba(40,40,48,0.78)');
      entered.append('text').style('font-family', MONO_FONT).style('font-size', '11px').attr('fill', 'rgba(255,255,255,0.92)');
      const merged = entered.merge(sel);
      merged.each(function positionOne(d) {
        const p = worldToScreen(d.x, d.y);
        const screenR = dampedRadius(d) * k;
        const label = truncateTitle(d.title, 30);
        const width = label.length * 5.8 + 12;
        // Top-right of the orb.
        const x0 = p.x + screenR * 0.4;
        const y0 = p.y - screenR - 20;
        const g = d3.select(this);
        g.select('rect').attr('x', x0).attr('y', y0).attr('width', width).attr('height', 16);
        g.select('text').attr('x', x0 + 6).attr('y', y0 + 11).text(label);
      });
    }

    const tick = () => {
      layersRef.current.nodeSel.attr('transform', d => `translate(${d.x},${d.y})`);
      updateNodeLabels();
    };

    // Where a node is pulled toward — the cluster's target center in the
    // default visualization, or its grid slot in the list visualization
    // (computed by computeListLayout, below). Switching vizStyle just swaps
    // what this reads; the existing simulation animates every node there the
    // same way it already animates a mode-change or a drag release.
    const targetFor = (d) => {
      if (vizStyleRef.current === 'list') {
        return listPositionsRef.current.get(d.id) || { x: 0, y: 0 };
      }
      return data.clusterCenters[d.cluster] || { x: 0, y: 0 };
    };
    const pullStrength = () => (vizStyleRef.current === 'list' ? 0.35 : 0.02);

    if (reducedMotionRef.current) {
      // Freeze at cached positions — no simulation at all.
      tick();
    } else {
      const simulation = d3.forceSimulation(simNodes)
        .force('x', d3.forceX(d => targetFor(d).x).strength(pullStrength))
        .force('y', d3.forceY(d => targetFor(d).y).strength(pullStrength))
        .force('collide', d3.forceCollide(d => radiusFor(d) + 3))
        .alphaTarget(DRIFT_ALPHA_TARGET)
        .alphaDecay(0)
        .velocityDecay(VELOCITY_DECAY)
        .on('tick', tick);
      simulationRef.current = simulation;
    }

    // Pan/zoom — default view fit to the current visualization's own bounds
    // with margin, so it always frames the full corpus regardless of size
    // (or, in list mode, the full grid).
    const zoomed = (event) => {
      zoomRoot.attr('transform', event.transform);
      transformRef.current = event.transform;
      updateSizesAndNumbers();
    };
    const zoomBehavior = d3.zoom().scaleExtent([0.15, 8]).on('zoom', zoomed);
    svg.call(zoomBehavior)
      .on('dblclick.zoom', null) // double-click is reserved for "reset", not d3's default zoom-in
      .on('dblclick', () => resetView(currentBounds()));
    zoomBehaviorRef.current = zoomBehavior;

    function currentBounds() {
      return vizStyleRef.current === 'list' && listBoundsRef.current ? listBoundsRef.current : data.bounds;
    }

    function computeFitTransform(bounds) {
      const { width, height } = dimensionsRef.current;
      const b = bounds;
      const contentW = Math.max(b.maxX - b.minX, 1);
      const contentH = Math.max(b.maxY - b.minY, 1);
      const margin = 0.85; // leave breathing room around the graph
      const bigBoost = bigModeRef.current ? BIG_MODE_ZOOM_MULTIPLIER : 1;
      const k = Math.min(width / contentW, height / contentH) * margin * bigBoost;
      const cx = (b.minX + b.maxX) / 2;
      const cy = (b.minY + b.maxY) / 2;
      return d3.zoomIdentity.translate(width / 2 - k * cx, height / 2 - k * cy).scale(k);
    }

    function resetView(bounds = currentBounds()) {
      const fit = computeFitTransform(bounds);
      defaultTransformRef.current = fit;
      d3.select(svgRef.current).transition().duration(reducedMotionRef.current ? 0 : 400).call(zoomBehavior.transform, fit);
    }

    function updateSizesAndNumbers({ animateRadius = false } = {}) {
      const showNumbers = modeRef.current === 'kaytto';
      layersRef.current.nodeSel.each(function updateOne(d) {
        const g = d3.select(this);
        const r = dampedRadius(d);
        const filteredOut = isFilteredOut(d);
        g.attr('opacity', filteredOut ? 0.08 : 1);
        // Keep the (larger, invisible) hit area at least as big as the
        // visible circle — normally HIT_RADIUS already dominates, but big
        // mode's multiplier can push the rendered circle past it.
        g.select('circle.v4-hit')
          .style('pointer-events', filteredOut ? 'none' : 'auto')
          .attr('r', Math.max(r, HIT_RADIUS));
        const body = g.select('circle.v4-body');
        if (animateRadius) body.transition().duration(250).attr('r', r);
        else body.interrupt().attr('r', r);
        const screenR = r * transformRef.current.k;
        const text = g.select('text.v4-number');
        if (showNumbers && screenR >= 9) {
          text.style('display', null)
            .style('font-size', `${Math.min(r * 0.9, 11)}px`)
            .text(d.breadth);
        } else {
          text.style('display', 'none');
        }
      });
      updateNodeLabels();
    }

    // d3-force's forceX/forceY/forceCollide only ever CALL their accessor
    // function once — when the force is attached to the simulation via
    // simulation.force(name, forceInstance) — and cache the resulting
    // per-node numbers from then on. Changing what targetFor()/radiusFor()
    // would now return (via modeRef/vizStyleRef/listPositionsRef) does
    // nothing to an already-running simulation on its own; the force has to
    // be re-attached so d3 re-reads it. (Verified directly against the
    // installed d3-force: same force instance, same simulation, changing the
    // closed-over value has zero effect until simulation.force() is called
    // again.) This is why neither the Käyttö collision growth nor the list
    // layout's target positions were taking effect before this fix.
    function reattachForces() {
      if (!simulationRef.current) return;
      simulationRef.current
        .force('x', d3.forceX(d => targetFor(d).x).strength(pullStrength))
        .force('y', d3.forceY(d => targetFor(d).y).strength(pullStrength))
        .force('collide', d3.forceCollide(d => radiusFor(d) + 3));
    }

    // Boosts the simulation's energy briefly so the collision force visibly
    // reacts and re-settles whenever a change moves every node's target at
    // once (a mode switch's radius change, or a viz-style/list-order
    // switch's target position change) — a static/instant jump read as
    // inert; this makes it feel like real physics responding to the change,
    // the same way a drag does.
    function boostPhysics() {
      if (reducedMotionRef.current || !simulationRef.current) return;
      reattachForces();
      clearTimeout(settleTimeoutRef.current);
      simulationRef.current.alphaTarget(MODE_SETTLE_ALPHA).restart();
      settleTimeoutRef.current = setTimeout(() => {
        simulationRef.current?.alphaTarget(DRIFT_ALPHA_TARGET);
      }, MODE_SETTLE_DURATION_MS);
    }

    // Recomputes the list grid (if entering/already in list mode), shows or
    // hides the cluster labels/backdrop (meaningless in list mode — nothing
    // is actually clustered), refits the view to whichever visualization is
    // now active, and animates every orb there via boostPhysics. Called once
    // up front and again whenever vizStyle/listOrder change (see the effect
    // below this scaffold effect).
    function applyVizStyle() {
      if (vizStyleRef.current === 'list') {
        // modeBRadius is always >= modeARadius by construction (the backend
        // guarantees Mode B is the larger of the two), so it alone is
        // enough headroom for either size mode's grid cell.
        const maxR = Math.max(...simNodes.map(n => n.modeBRadius));
        const { positions, bounds } = computeListLayout(simNodes, listOrderRef.current, data.clusterOrder, maxR * 2 + 14);
        listPositionsRef.current = positions;
        listBoundsRef.current = bounds;
      }
      layers.clusterLayer.transition().duration(300).attr('opacity', vizStyleRef.current === 'list' ? 0 : 1);
      resetView(currentBounds());
      boostPhysics();
    }

    // Big mode now feeds real, steeply-scaled radii (bigModeRadius, up to a
    // 12:1 max:min ratio) straight into radiusFor/the collision force, so
    // toggling it genuinely changes how much room nodes need — boostPhysics
    // re-attaches the forces (picking up the new radii) and gives the sim
    // enough energy to visibly push everything apart to stay separate,
    // exactly like a mode switch or a drag does.
    function applyBigMode() {
      updateSizesAndNumbers({ animateRadius: true });
      boostPhysics();
      resetView(currentBounds());
    }

    // Initial fit + initial size/number pass
    resetView(currentBounds());
    updateSizesAndNumbers();
    layersRef.current.updateSizesAndNumbers = updateSizesAndNumbers;
    layersRef.current.boostPhysics = boostPhysics;
    layersRef.current.applyVizStyle = applyVizStyle;
    layersRef.current.applyBigMode = applyBigMode;

    return () => {
      simulationRef.current?.stop();
      simulationRef.current = null;
      clearTimeout(settleTimeoutRef.current);
      svg.on('.zoom', null);
      zoomBehaviorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Mode toggle: radius transitions in place, and — since the layout is only
  // ever pre-spaced for Mode A's smaller uniform radius — Mode B's larger
  // radii genuinely overlap at first, so boosting the simulation's alpha
  // here makes the collision force visibly push orbs apart to resolve it,
  // rather than a static resize.
  useEffect(() => {
    layersRef.current?.updateSizesAndNumbers?.({ animateRadius: true });
    layersRef.current?.boostPhysics?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Big mode: animates every orb's rendered radius up (and zooms in to
  // match) without touching positions or the collision force — see
  // applyBigMode's comment for why that's safe to do purely cosmetically.
  useEffect(() => {
    layersRef.current?.applyBigMode?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bigMode]);

  // Visualization style / list ordering: recompute the target layout (grid
  // slots or back to cluster centers) and let the same physics carry every
  // orb there, per the request that switching styles animates into position
  // rather than jumping.
  useEffect(() => {
    layersRef.current?.applyVizStyle?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vizStyle, listOrder]);

  // Teema filter: only opacity/hit-area changes, no radius or physics involved.
  useEffect(() => {
    layersRef.current?.updateSizesAndNumbers?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teemaFilter]);

  const toggleTeema = (teema) => {
    setTeemaFilter(prev => {
      const next = new Set(prev);
      if (next.has(teema)) next.delete(teema); else next.add(teema);
      return next;
    });
  };

  const teemaKeysPresent = data ? [...new Set(data.nodes.map(n => n.teema).filter(Boolean))] : [];

  return (
    <Container ref={containerRef}>
      <SVGContainer ref={svgRef} />

      {!embedded && (
        <TopLeftBar>
          {status === 'ready' && (
            <Title>{data.title ? `${data.title} tietopohja` : 'Tietopohja'}</Title>
          )}
          <InstanceSelect
            value={instanceId || ''}
            onChange={(e) => handleInstanceChange(e.target.value)}
          >
            <option value="" disabled>{t('ragSourceMapV4.pickInstance')}</option>
            {instances.map(inst => (
              <option key={inst.id} value={inst.id}>{inst.title}</option>
            ))}
          </InstanceSelect>
        </TopLeftBar>
      )}

      {status === 'loading' && <StatusMessage>{t('ragSourceMapV2.loading')}</StatusMessage>}
      {status === 'empty' && <StatusMessage>{t('ragSourceMapV2.empty')}</StatusMessage>}
      {status === 'error' && <StatusMessage>{t('ragSourceMapV2.error')}</StatusMessage>}

      {status === 'ready' && (
        <>
          <TopRightControls>
            <ModeButton $active={mode === 'lahteet'} onClick={() => setMode('lahteet')}>
              {t('ragSourceMapV4.modeLahteet')}
            </ModeButton>
            <ModeButton $active={mode === 'kaytto'} onClick={() => setMode('kaytto')}>
              {t('ragSourceMapV4.modeKaytto')}
            </ModeButton>
          </TopRightControls>

          {mode === 'kaytto' && <UsageStatsPanel data={data} teemaFilter={teemaFilter} t={t} />}

          {vizStyle === 'list' && (
            <ListOrderBar>
              {['theme', 'size', 'random', 'cluster'].map(order => (
                <ListOrderButton
                  key={order}
                  $active={listOrder === order}
                  onClick={() => setListOrder(order)}
                >
                  {t(`ragSourceMapV4.listOrder.${order}`)}
                </ListOrderButton>
              ))}
            </ListOrderBar>
          )}

          {teemaKeysPresent.length > 0 && (
            <FilterBar>
              {teemaKeysPresent.map(teema => (
                <FilterChip key={teema} $active={teemaFilter.has(teema)} onClick={() => toggleTeema(teema)}>
                  {t(`ragSourceMapV4.filters.${teema}`)}
                </FilterChip>
              ))}
            </FilterBar>
          )}

          <StyleSwitcher>
            <StyleButton
              $active={vizStyle === 'cluster'}
              title={t('ragSourceMapV4.vizCluster')}
              aria-label={t('ragSourceMapV4.vizCluster')}
              onClick={() => setVizStyle('cluster')}
            >
              ⁂
            </StyleButton>
            <StyleButton
              $active={vizStyle === 'list'}
              title={t('ragSourceMapV4.vizList')}
              aria-label={t('ragSourceMapV4.vizList')}
              onClick={() => setVizStyle('list')}
            >
              ☰
            </StyleButton>
            <StyleButton
              $active={bigMode}
              title={t('ragSourceMapV4.vizBig')}
              aria-label={t('ragSourceMapV4.vizBig')}
              onClick={() => setBigMode(v => !v)}
            >
              ⬤
            </StyleButton>
          </StyleSwitcher>
        </>
      )}

      {selectedNode && (() => {
        const accent = data.clusterColors[selectedNode.cluster];
        const isBase = selectedNode.cluster === data.perustietopohjaKey;
        const clusterLabel = isBase
          ? t('ragSourceMapV2.perustietopohja')
          : t(`ragSourceMapV2.voices.${selectedNode.cluster}`);
        const meta = [selectedNode.publisher, selectedNode.publicationYear].filter(Boolean).join(' · ');
        return (
          <DetailPanel ref={detailPanelRef} accent={accent}>
            <CloseButton onClick={() => setSelectedNode(null)}>&times;</CloseButton>
            <DetailTitle>{selectedNode.title}</DetailTitle>
            <DetailType accent={accent}>{clusterLabel}</DetailType>

            {meta && <DetailMeta>{meta}</DetailMeta>}
            {selectedNode.description && <DetailDescription>{selectedNode.description}</DetailDescription>}

            <DetailTotal>{selectedNode.breadth}</DetailTotal>
            <DetailTotalLabel>{t('ragSourceMapV4.breadthLabel')}</DetailTotalLabel>
            <div>
              {Object.entries(selectedNode.byContextBreadth || {})
                .sort((a, b) => b[1] - a[1])
                .map(([context, count]) => (
                  <ContextRow key={context}>
                    <span>{contextLabel(t, context)}</span>
                    <span>{count}</span>
                  </ContextRow>
                ))}
            </div>
          </DetailPanel>
        );
      })()}
    </Container>
  );
}

export default CorpusTietopohjaV4Screen;
